import _ from 'lodash';
import { defaultUiBehaviourFlags, IntentLabelConfig } from 'v2/modules/withRunForm/configs/index';
import { setPartialQuantConfig } from 'v2/modules/withRunForm/redux/actions';
import { getSelectorBasedStoreValue } from 'v2/modules/withRunForm/redux/selector';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RUN_TYPES } from 'v2/common/constants/index';
import { resetLabels } from 'v2/modules/withRunForm/events/resetLabels';
import HoverMessage from 'common/components/HoverMessage';
import QuantityReset from 'v2/modules/withRunForm/components/FeatureEnablements/QuantityReset/index';
import { Dropdown } from 'react-bootstrap';
import { reactToastify, TOASTIFY_TYPES } from 'common/utils/reactToastify';
import { onChangeUiBehaviourFlags } from '../../events/uiBehaviourFlags';

const propTypes = {};
const defaultProps = {};

const FeatureEnablements = () => {
  const uiBehaviourFlags =
    useSelector((state) => getSelectorBasedStoreValue(state, 'quantConfig.uiBehaviourFlags')) ||
    defaultUiBehaviourFlags;
  const runType = useSelector((state) => getSelectorBasedStoreValue(state, 'runType'));
  const cases = useSelector((state) => getSelectorBasedStoreValue(state, 'quantConfig.cases'));
  const pipeConfigs = useSelector((state) => getSelectorBasedStoreValue(state, 'quantConfig.pipeConfigs'));

  const { isReEntry, isReExecute, isTrailingStopLoss, isWaitAndTrade } = uiBehaviourFlags;
  const dispatch = useDispatch();

  const onUpdateUiBehaviourFlags = useCallback(
    (updateValues) => {
      const newUiBehaviourFlags = { ...uiBehaviourFlags, ...updateValues };
      dispatch(
        setPartialQuantConfig(onChangeUiBehaviourFlags({ cases, uiBehaviourFlags: newUiBehaviourFlags }))
      );
    },
    [cases, dispatch, uiBehaviourFlags]
  );

  const isShowWaitAndTrade = useMemo(
    () =>
      runType === RUN_TYPES.live
        ? _.get(window, 'userPermissions.waitAndTrade.isEnabled', false)
        : _.get(window, 'userPermissions.waitAndTradeForBacktest.isEnabled', false),
    [runType]
  );

  const handelLablesIndexReset = useCallback(() => {
    try {
      const { updatedCases, updatedPipeConfigs, lastLabels } = resetLabels(cases, pipeConfigs);
      dispatch(
        setPartialQuantConfig({
          cases: updatedCases,
          pipeConfigs: updatedPipeConfigs,
        })
      );

      _.forEach(lastLabels, (label, caseIndex) => {
        IntentLabelConfig.setConfigNameAndValue(caseIndex, label);
      });
      reactToastify('Leg Labels Reset Successfully', TOASTIFY_TYPES.INFO);
    } catch (e) {
      console.log('Error occured during leg reset call', e);
      reactToastify('Something went wrong unable to Reset Legs ID', TOASTIFY_TYPES.ERROR);
    }
  }, [cases, dispatch, pipeConfigs]);

  const reEntryContent = (
    <div>No re-entry happens if the trailing stop loss or move to cost is already triggered.</div>
  );

  const reExecuteContent = (
    <div>No re-execute happens if the trailing stop loss or move to cost is already triggered.</div>
  );

  const moreFeatures = useMemo(() => {
    return (
      <Dropdown align="end" drop="down" className="more-features pb-2">
        <Dropdown.Toggle variant="primary" className="btn btn-outline-primary float-right ">
          More
        </Dropdown.Toggle>

        <Dropdown.Menu align="start">
          <QuantityReset />
          <Dropdown.Item onClick={handelLablesIndexReset}>Reset Leg ID</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [handelLablesIndexReset]);

  const featureConfigs = useMemo(
    () => [
      {
        label: 'Trailing Stop Loss',
        isPermissionToShow: _.get(window, 'userPermissions.trailingStopLoss.isEnabled', false),
        isChecked: isTrailingStopLoss,
        id: 'isTrailingStopLoss',
        updateValues: { isTrailingStopLoss: !isTrailingStopLoss },
        isdisabled: false,
      },
      {
        label: 'Re-Entry',
        isPermissionToShow: _.get(window, 'userPermissions.reEntry.isEnabled', false),
        isChecked: isReEntry,
        id: 'isReEntry',
        updateValues: { isReEntry: !isReEntry },
        isdisabled: false,
        infoIcon: (
          <HoverMessage message={reEntryContent}>
            <i className="fa-regular fa-circle-info info-icon tx-14 ms-1 me-3"></i>
          </HoverMessage>
        ),
      },
      {
        label: 'Re-Execute',
        isPermissionToShow: _.get(window, 'userPermissions.reExecute.isEnabled', false),
        isChecked: isReExecute,
        id: 'isReExecute',
        updateValues: { isReExecute: !isReExecute },
        isdisabled: false,
        infoIcon: (
          <HoverMessage message={reExecuteContent}>
            <i className="fa-regular fa-circle-info info-icon tx-14 ms-1 me-3"></i>
          </HoverMessage>
        ),
      },
      {
        label: 'Wait and Trade',
        isPermissionToShow: isShowWaitAndTrade,
        isChecked: isWaitAndTrade,
        id: 'isWaitAndTrade',
        updateValues: { isWaitAndTrade: !isWaitAndTrade },
        isdisabled: false,
      },
    ],
    [
      isReEntry,
      isReExecute,
      isShowWaitAndTrade,
      isTrailingStopLoss,
      isWaitAndTrade,
      reEntryContent,
      reExecuteContent,
    ]
  );

  const renderFeatureConfig = useCallback(
    ({ label, isPermissionToShow, isChecked, id, isdisabled, updateValues, infoIcon }) => {
      if (!isPermissionToShow) return null;

      return (
        <div key={id} className="d-flex align-items-center">
          <div className="form-check">
            <input
              className="form-check-input track"
              type="checkbox"
              data-track-category="Advanced Features Enablement"
              data-track-action={label}
              data-track-label={label}
              disabled={isdisabled}
              id={id}
              onClick={() => {}}
              onChange={() => onUpdateUiBehaviourFlags(updateValues)}
              checked={isChecked}
            />
            <label className="form-check-label feature-enablement-label" htmlFor={id} disabled={isdisabled}>
              {label}
            </label>
          </div>
          {infoIcon && <>{infoIcon}</>}
        </div>
      );
    },
    [onUpdateUiBehaviourFlags]
  );

  return (
    <div className="features-enablement-details ">
      <div className="features-enablement-container pr-4">
        <div className="feature-content-panel">
          <div className="selector-text">
            Features Enablement
            <span className="ms-1">:</span>
          </div>
          <div className="d-flex justify-content align-items-center  flex-wrap feature-content">
            {_.map(featureConfigs, renderFeatureConfig)}
          </div>
        </div>
        {moreFeatures}
      </div>
    </div>
  );
};

FeatureEnablements.propTypes = propTypes;
FeatureEnablements.defaultProps = defaultProps;

export default FeatureEnablements;
